import { NextRouter } from 'next/router';
import {
  DateFormat,
  IKeyStringValue,
  MEMBER_FARE_CUT_LINE,
} from 'common/types';
import {
  days,
  getDurationKr,
  getMinutes,
  hasTravelClubFare,
} from 'common/utils';
import { IAirportDetail } from 'hooks/useAirportDetailList';
import {
  IFare,
  IFareDetail,
  IFareTypeList,
  ISchedule,
  IScheduleDetail,
  ISchedules,
  ScheduleKeys,
} from 'list/international/types';
import {
  getPriceOfPerson,
  getTripStringFromRoutes,
  hasAdultNaverFare,
  toDays,
} from 'list/international/utils';
import {
  AgreementStatus,
  ICardFareTypeItem,
  ICardFareTypeMap,
  IOta,
  IOtaFareList,
  IPassengerCount,
} from './types';

export const areaCode: string = 'idt';

export function getCookie(cookie: string, name: string) {
  const value = cookie.match(`(^|;) ?${name}=([^;]*)(;|$)`);
  return value ? value[2] : null;
}

export const showFlightTime = (hhmm: string) => {
  const h = hhmm.substr(0, 2);
  const m = hhmm.substr(2, 2);
  return `${h}시간 ${m}분`;
};

export const showFlightTimeFromDetail = (arr: IScheduleDetail[]): string =>
  getDurationKr(
    arr.reduce<number>(
      (res, item: any) => res + getMinutes(item.ft) + getMinutes(item.ct),
      0,
    ),
  );

export const getPriceByCount = (
  fare: IFare,
  cnt: number,
  isNaverFarePromotion?: boolean,
) => getPriceOfPerson(fare, isNaverFarePromotion) * cnt;

export const getTotalPrice = (
  fare: IFareDetail,
  adultCnt: number,
  childCnt: number,
  infantCnt: number,
  isNaverFare?: boolean,
) => {
  const { Adult, Child, Infant } = fare;
  const isDiscountPromotion = isNaverFare && hasAdultNaverFare(fare);
  return (
    getPriceByCount(Adult, adultCnt, isDiscountPromotion) +
    getPriceByCount(Child, childCnt, isDiscountPromotion) +
    getPriceByCount(Infant, infantCnt, isDiscountPromotion)
  );
};

export const getCardFareTypeMap = (
  selectedFares: IFareTypeList,
  lastestCardFareType: string,
) => {
  return Object.keys(selectedFares)?.reduce<ICardFareTypeMap>(
    (result, keyName) => {
      // {A01:true, A01/B05:false ...}
      return {
        ...result,
        [keyName]: keyName === lastestCardFareType,
      };
    },
    { all: false },
  );
};

export const getUncheckedCount = (checkCardFareTypeMap: ICardFareTypeMap) => {
  // 다른요금조건 선택 개수
  if (checkCardFareTypeMap.all) return 0;
  const list = Object.entries(checkCardFareTypeMap);
  const checkedCount: number = list.reduce(
    (result: number, [key, value]: any) => {
      let res = result;
      if (key !== 'all' && value) res++;
      return res;
    },
    0,
  );
  const unCheckedCount = list.length - 1 - checkedCount;
  return unCheckedCount;
};

// checkCardFareTypeMap - {all: false, A01: true, A01/B01: false ...}
// selectedCardFares - { A01/B01:	[{ Adult: {}, Child: {}, Infant: {}, AgtCode:'INT005', ...}, {...}, ...], A01: ... }
export const findFareList = (
  checkCardFareTypeMap: ICardFareTypeMap,
  selectedCardFares: IFareTypeList,
  adultCnt: number,
  childCnt: number,
  infantCnt: number,
) => {
  return Object.entries(selectedCardFares)
    .reduce<IFareDetail[]>((result, [key, values]: [string, IFareDetail[]]) => {
      if (checkCardFareTypeMap[key] || checkCardFareTypeMap.all) {
        const replaced = replaceNGMemberFare(values);
        result.push(...replaced);
      }
      return result;
    }, [])
    .sort((a, b) => {
      return (
        getTotalPrice(a, adultCnt, childCnt, infantCnt, true) -
        getTotalPrice(b, adultCnt, childCnt, infantCnt, true)
      );
    });
};

export const replaceNGMemberFare = (fareDataList: IFareDetail[]) => {
  const replacedFareList = fareDataList.map((fare, index) => {
    if (fare.isMemberFare && index >= MEMBER_FARE_CUT_LINE && fare.Org) {
      const { Adult, Child, Infant, ReserveParameter } = fare.Org;
      return {
        ...fare,
        isMemberFare: false,
        Adult,
        Child,
        Infant,
        ReserveParameter,
      };
    }
    return fare;
  });

  return replacedFareList;
};

// 마지막 아이템과 같은 가격이 있으면 추가
export const getShowAgentList = (
  fareList: IOtaFareList[],
  num: number,
  adultCnt: number,
  childCnt: number,
  infantCnt: number,
) => {
  const list = fareList?.slice(0, num);
  fareList?.slice(num).forEach(item => {
    if (
      getTotalPrice(item.fare, adultCnt, childCnt, infantCnt, true) ===
      getTotalPrice(
        list[list.length - 1].fare,
        adultCnt,
        childCnt,
        infantCnt,
        true,
      )
    ) {
      list.push(item);
    }
  });
  return list;
};

export const mergeOtaFareList = (
  otaList: IOta[],
  fareDataList: IFareDetail[],
) => {
  return otaList.reduce<IOtaFareList[]>((result, ota) => {
    const findItem = fareDataList.find((item: any) => {
      return ota.otaId === item.AgtCode && ota.fareType === item.FareType;
    });
    if (findItem) {
      result.push({ ota, fare: findItem });
    }
    return result;
  }, []);
};

export const getParamSelectedFlight = (str: string) => {
  const splitArr = decodeURIComponent(str).split(',');
  return splitArr.map(item => {
    const [order, scheduleId, cardFareType, active, airlineCode] =
      item.split(':');
    return {
      order,
      scheduleId: scheduleId?.replace(/ /g, '+'),
      cardFareType,
      active,
      airlineCode,
    };
  });
};

export const getScheduleData = (
  schedules: ISchedules[],
  selectedScheduleIds: string[],
) => schedules?.map((item, idx) => item[selectedScheduleIds[idx]]);

export const setChecklistToggleAll = (
  checkedList: ICardFareTypeMap,
  setCheckedList: React.Dispatch<React.SetStateAction<ICardFareTypeMap>>,
) => {
  if (checkedList.all) return;
  const newCheckedList = { ...checkedList };
  Object.keys(newCheckedList).forEach(key => {
    newCheckedList[key] = key === 'all';
  });
  setCheckedList(newCheckedList);
};

export const setChecked = (
  checkedList: ICardFareTypeMap,
  type: string,
  setCheckedList: React.Dispatch<React.SetStateAction<ICardFareTypeMap>>,
) => {
  const newCheckedList = {
    ...checkedList,
    [type]: !checkedList[type],
  };
  delete newCheckedList.all;
  newCheckedList.all = Object.values(newCheckedList).every(value => !value);
  setCheckedList(newCheckedList);
};

export const getLowPriceMap = (
  selectedCardFares: IFareTypeList,
  passengerCount: IPassengerCount,
): ICardFareTypeItem[] => {
  // 각 카드요금조건에서 최저가
  const { adultCnt, childCnt, infantCnt } = passengerCount;
  const lowPriceFares = Object.entries(selectedCardFares).reduce<
    ICardFareTypeItem[]
  >((result, [key, value]) => {
    result.push({
      key,
      otaId: value[0].AgtCode,
      price: getTotalPrice(value[0], adultCnt, childCnt, infantCnt),
      naverFare: hasAdultNaverFare(value[0])
        ? getTotalPrice(value[0], adultCnt, childCnt, infantCnt, true)
        : 0,
      fareDetail: value[0],
      hasTravelClubFare: hasTravelClubFare(value),
    });
    return result;
  }, []);
  return lowPriceFares.sort(
    (a, b) =>
      (a.naverFare ? a.naverFare : a.price) -
      (b.naverFare ? b.naverFare : b.price),
  );
};

export const getCheckedCardFareTypeName = (
  checkedMap: ICardFareTypeMap,
  korFareTypes: IKeyStringValue,
): string => {
  return (
    Object.entries(checkedMap)
      .reduce<string[]>(
        (result, [key, value]) =>
          !value
            ? result
            : [...result, key === 'all' ? '전체' : korFareTypes[key]],
        [],
      )
      .join(', ') || ''
  );
};

/* ['ICN-KIX-20200610', 'KIX-ICN-20200620'] -> 
  {
    airportList: ['ICN', 'KIX', 'KIX', 'ICN'],
    dateList: ['20200610', 20200620'],
    cityList: ['서울', '오사카', '오사카', '서울']
  } */

export const getItineraryInfo = (
  routes: string[],
  airports: Map<string, IAirportDetail>,
) => {
  return routes.reduce<{
    airportList: string[];
    dateList: string[];
    cityList: string[];
  }>(
    (result, item) => {
      const [startCode, endCode, date] = item.split('-');
      const transDate = date.replace(/(\d{4})(\d{2})(\d{2})/, '$1.$2.$3.');
      result.airportList.push(startCode);
      result.airportList.push(endCode);
      result.cityList.push(airports.get(startCode)?.cityName || '');
      result.cityList.push(airports.get(endCode)?.cityName || '');
      result.dateList.push(transDate);
      return result;
    },
    { airportList: [], cityList: [], dateList: [] },
  );
};

/* const getSelectedFlightUrl = (selectedFlightList: any) => {
  const urlList = selectedFlightList.map((item: any, idx: number) => {
    const { order, scheduleId, cardFareType, active, airlineCode } = item;
    return `${order}:${scheduleId}:${cardFareType}:${active}:${airlineCode}`;
  });
  return urlList.join(',');
};

export const getRemoveScheduleList = (
  selectedFlightList: any,
  router: any,
  selectType: string,
) => {
  return selectedFlightList.map((item: object, idx: number) =>
    getLink(
      getSelectedFlightUrl(selectedFlightList.slice(0, idx)),
      router,
      selectType,
    ),
  );
}; */

export const getFavoriteParam = ({
  router,
  seatClass,
  korFareTypes,
  selectedFareItem,
  selectedSchedules,
  airlines,
  airports,
  passengerCount,
}: {
  router: NextRouter;
  seatClass: string;
  korFareTypes: IKeyStringValue;
  selectedFareItem: IFareDetail;
  selectedSchedules: ISchedule[];
  airlines: IKeyStringValue;
  airports: Map<string, IAirportDetail>;
  passengerCount: IPassengerCount;
}) => {
  const { routes, selectedFlight } = router.query;
  const { adultCnt, childCnt, infantCnt } = passengerCount;
  const { airportList, dateList, cityList } = getItineraryInfo(
    routes as string[],
    airports,
  );
  const groupParam = {
    airports: airportList,
    cities: cityList,
    sdates: dateList,
    tripTp: getTripStringFromRoutes(routes as string[]),
    fareTp: seatClass,
    adultCnt,
    childCnt,
    infantCnt,
    ticketTp: 'rt', // realtime
  };
  const selectedList = (selectedFlight as string).split(',');
  const selectedFareType = selectedList[selectedList.length - 1].split(':')[2];
  const selectedFareTypeKor = korFareTypes[selectedFareType] || 'unknown';
  const gidPattern = /&gid=[\w\d%]*/g;

  const flightData = {
    seatSt: 'HK',
    fare: getPriceOfPerson(selectedFareItem.Adult, true),
    fareTp: selectedFareTypeKor,
    serviceUrl: window.location.href.replace(gidPattern, ''),
    itin: selectedSchedules.map(({ detail }) => {
      const sdtDay = toDays(detail[0].sdt);
      const edtDay = toDays(detail[detail.length - 1].edt);
      const dayDiff = edtDay.startOf('d').diff(sdtDay.startOf('d'), 'd');
      return {
        airlineCd: detail[0].av,
        airlineName: airlines[detail[0].av],
        depAp: detail[0].sa,
        arrAp: detail[detail.length - 1].ea,
        depDt: sdtDay.format(DateFormat.DotYMD),
        arrDt: edtDay.format(DateFormat.DotYMD),
        depTm: sdtDay.format('HH:mm'),
        arrTm: edtDay.format('HH:mm'),
        jrnyTm: showFlightTimeFromDetail(detail),
        viaCnt: detail.length - 1,
        dayCnt: dayDiff,
      };
    }),
  };
  return { groupParam, flightData };
};

export const getExtraParams = (
  schedule: IScheduleDetail[],
  router: NextRouter,
) => {
  const {
    query: { fareType },
  } = router;
  const mainAvItem = schedule.find((item: any) => item.im) || schedule[0];
  const param = {
    airline: mainAvItem.av,
    departure: schedule[0].sa,
    arrival: schedule[schedule.length - 1].ea,
    searchDate: days(new Date()).format('YYYYMMDD'),
    seatGrade: fareType || 'Y',
    segs: schedule.map(item => {
      return {
        airV: item[ScheduleKeys.airlineVendor],
        dep: item[ScheduleKeys.startAirport],
        arr: item[ScheduleKeys.endAirport],
        bookingClass: '',
        equipment: item[ScheduleKeys.planeInfo] || '',
        fltNum: item[ScheduleKeys.flightNumber],
        startDate: toDays(item[ScheduleKeys.startDateTime]).format('YYYYMMDD'),
      };
    }),
  };
  return param;
};

export enum Validation {
  AGREEMENT_REQUEST = 'AGREEMENT_REQUEST',
  BOOKING_AVAILABLE = 'BOOKING_AVAILABLE',
  LOGIN_REQUIRED = 'LOGIN_REQUIRED',
}

const otasRequiringAgreement = new Set<string>();
const statusesRequiringAgreement = new Set([
  AgreementStatus.EXPIRED,
  AgreementStatus.NOT_CONFIRMED,
]);

export function validateAgreeStatus(
  otaId: string,
  agreeStatus: AgreementStatus,
): [Validation, boolean] {
  const isAgreementRequired = otasRequiringAgreement.has(otaId);
  if (isAgreementRequired && agreeStatus === AgreementStatus.NOT_LOGGED_IN) {
    return [Validation.LOGIN_REQUIRED, isAgreementRequired];
  }
  if (
    (isAgreementRequired && agreeStatus === AgreementStatus.NOT_AGREED) ||
    statusesRequiringAgreement.has(agreeStatus)
  ) {
    return [Validation.AGREEMENT_REQUEST, isAgreementRequired];
  }
  return [Validation.BOOKING_AVAILABLE, isAgreementRequired];
}
